import { TranslationService } from "../../../../services/TranslationService";
import {
  formatDateDigits,
  formatDateTimeShort,
  formatInteger,
  formatNumberFractions
} from "../../../../utils/FormatUtils";
import { restTimezone } from "../../../../utils/ParseUtils";
import {
  AIAgentType,
  AIBillingErrorKind,
  AIBillingStatus
} from "../../../../services/AIBillingService";

export const AI_BILLING_CURRENCY_LABEL = "USD";

export const AI_BILLING_AMOUNT_FRACTIONS = 2;

export const AI_BILLING_COST_FRACTIONS = 4;

export function formatAmountUsd(
  value: number,
  fractions: number = AI_BILLING_AMOUNT_FRACTIONS
): string {
  return `${AI_BILLING_CURRENCY_LABEL} ${formatNumberFractions(value, fractions)}`;
}

export function formatSignedAmountUsd(value: number): string {
  return `+ ${formatAmountUsd(value)}`;
}

export function formatCostUsd(value: number | null): string {
  if (value === null) {
    return "";
  }
  return formatNumberFractions(value, AI_BILLING_COST_FRACTIONS);
}

export function formatTokens(value: number): string {
  return formatInteger(value);
}

export function formatPercentage(value: number): string {
  return `${formatNumberFractions(value, 1)}%`;
}

export function formatIsoDate(iso: string): string {
  return formatDateDigits(restTimezone(new Date(iso)));
}

export function formatIsoDateTime(iso: string): string {
  return formatDateTimeShort(restTimezone(new Date(iso)));
}

type StatusVariant = "success" | "danger" | "warning" | "secondary";

const agentStatusVariants: Record<AIBillingStatus, StatusVariant> = {
  not_configured: "secondary",
  disabled: "secondary",
  blocked: "danger",
  low_balance: "warning",
  active: "success"
};

export function getAgentStatusVariant(status: AIBillingStatus): StatusVariant {
  return agentStatusVariants[status] ?? "secondary";
}

export function getAgentStatusLabel(status: AIBillingStatus): string {
  const { translate } = TranslationService;
  const labels: Record<AIBillingStatus, string> = {
    not_configured: translate.AIUsageStatusNotConfigured,
    disabled: translate.AIUsageStatusDisabled,
    blocked: translate.AIUsageStatusBlocked,
    low_balance: translate.AIUsageStatusLowBalance,
    active: translate.AIUsageStatusActive
  };
  return labels[status] ?? status;
}

export function getAgentStatusNote(status: AIBillingStatus): string {
  const { translate } = TranslationService;
  const notes: Record<AIBillingStatus, string> = {
    not_configured: translate.AIUsageStatusNotConfiguredNote,
    disabled: translate.AIUsageStatusDisabledNote,
    blocked: translate.AIUsageStatusBlockedNote,
    low_balance: translate.AIUsageStatusLowBalanceNote,
    active: translate.AIUsageStatusActiveNote
  };
  return notes[status] ?? "";
}

export function isAgentStatusBlocking(status: AIBillingStatus): boolean {
  return (
    status === "blocked" || status === "disabled" || status === "not_configured"
  );
}

export function getErrorMessage(kind: AIBillingErrorKind): string {
  const { translate } = TranslationService;
  return kind === "forbidden"
    ? translate.AINoPermissionToView
    : translate.ErrorLoadingData;
}

export function getAgentTypeLabel(agentType: AIAgentType | null): string {
  if (agentType === null) {
    return "";
  }
  const { translate } = TranslationService;
  const labels: Record<AIAgentType, string> = {
    [AIAgentType.Chat]: translate.AIUsageAgentChat,
    [AIAgentType.ChatTitle]: translate.AIUsageAgentChatTitle,
    [AIAgentType.Categorize]: translate.AIUsageAgentCategorize,
    [AIAgentType.Review]: translate.AIUsageAgentReview,
    [AIAgentType.Translate]: translate.AIUsageAgentTranslate,
    [AIAgentType.Retenciones]: translate.AIUsageAgentRetenciones
  };
  return labels[agentType] ?? String(agentType);
}

export function resolveUserName(
  userId: number,
  users: { Id: string; Value: string }[] | undefined
): string {
  const { translate } = TranslationService;
  return (
    users?.find((x) => Number(x.Id) === userId)?.Value ?? translate.AIAgent
  );
}
